import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export default function HealthIndicator() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'healthy' | 'unhealthy'>('loading');

  useEffect(() => {
    const checkHealth = async () => {
      try {
        await api.getHealth();
        setStatus('healthy');
      } catch {
        setStatus('unhealthy');
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const statusConfig = {
    loading: { color: 'bg-slate-400', label: t('healthIndicator.checking') },
    healthy: { color: 'bg-green-500', label: t('healthIndicator.connected') },
    unhealthy: { color: 'bg-red-500', label: t('healthIndicator.disconnected') },
  };

  const config = statusConfig[status];

  // Healthy/loading: dot only (with tooltip). Unhealthy: dot + label (loud failure).
  if (status === 'unhealthy') {
    return (
      <div className="flex items-center gap-1.5" title={config.label}>
        <div className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
        <span className="text-sm font-medium text-red-600 dark:text-red-400">
          {config.label}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center" title={config.label} aria-label={config.label}>
      <div className="relative">
        <div className={`w-2.5 h-2.5 rounded-full ${config.color}`} />
        {status === 'healthy' && (
          <div
            className={`absolute inset-0 w-2.5 h-2.5 rounded-full ${config.color} animate-ping opacity-75`}
          />
        )}
      </div>
    </div>
  );
}
